import t from "@constants/localization";
import { colors } from "@constants/theme";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import InfoTooltipModal from "@screens/MyDeliveries/components/InfoTooltipModal";
import { getPreviousCrateDeliveries } from "@services/api";
import React, { useCallback, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { getDayMonth } from "@utils/getFormattedDate";
import NoDeliveryCard from "../DeliveryReturn/NoDeliveryCard";
import ReturnCard from "./ReturnCard";
import ListEmptyComponent from "../components/ListEmptyComponent";
import ReturnProductInfoCard from "../components/ReturnProductInfoCard";
import DeliveryCard from "./DeliveryCard";

const PATH = "screens/MyDeliveries/DeliveryReturn/OverView";

const Overview = () => {
  const isFocused = useIsFocused();

  const [deliveriesData, setDeliveriesData] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [deliveryState, setDeliveryState] = useState();
  const [infoTooltipVisible, setInfoTooltipVisible] = useState(false);
  const [infoTooltipData, setInfoTooltipData] = useState({});

  useFocusEffect(
    useCallback(() => {
      if (isFocused) {
        fetchData();
      }
    }, [isFocused]),
  );

  const fetchData = async () => {
    try {
      const res = await getPreviousCrateDeliveries();
      const vacationingDataExcluded = res?.data?.filter(
        (item) => item?.state !== "Vacationing",
      );
      setDeliveriesData(vacationingDataExcluded);
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
    setIsRefreshing(false);
  };

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  const RenderSectionHeader = () => {
    return (
      <View style={styles.sectionHeaderContainer}>
        <View style={styles.dashedLine} />
        <View style={styles.sectionHeaderTextContainer}>
          <Text style={styles.sectionHeaderText}>
            {t(`${PATH}/sectionHeader`)}
          </Text>
        </View>
        <View style={styles.dashedLine} />
      </View>
    );
  };

  const renderItem = useCallback(
    ({ item }) => {
      if (item?.type === "sectionHeader") {
        return <RenderSectionHeader />;
      } else {
        return (
          <>
            <View style={styles.sectionHeader}>
              <Text style={styles.dateText}>{getDayMonth(item?.date)}</Text>
            </View>
            {item?.deliveryDetails ? (
              <DeliveryCard
                deliveryDetails={item?.deliveryDetails}
                deliveryBoyInfo={item?.deliveryBoyInfo}
                spokeName={item?.spokeName}
                deliveriesData={deliveriesData}
                setInfoTooltipVisible={setInfoTooltipVisible}
                setInfoTooltipData={setInfoTooltipData}
              />
            ) : (
              <NoDeliveryCard />
            )}
            {item?.returnDetails ? (
              <ReturnCard
                returnDetails={item?.returnDetails}
                date={item?.date}
                spokeName={item?.spokeName}
                setInfoTooltipVisible={setInfoTooltipVisible}
                setInfoTooltipData={setInfoTooltipData}
              />
            ) : (
              <></>
            )}
          </>
        );
      }
    },
    [deliveriesData],
  );

  const keyExtractor = (item, index) => `${item?.date}-${index}`;

  if (isLoading) {
    return (
      <View style={styles.loadingView}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <InfoTooltipModal
        visible={infoTooltipVisible}
        setVisible={setInfoTooltipVisible}
        data={infoTooltipData}
      />
      <FlatList
        data={deliveriesData}
        renderItem={renderItem}
        ListHeaderComponent={ReturnProductInfoCard({
          data: deliveriesData?.spokeDetails,
        })}
        ListEmptyComponent={ListEmptyComponent({ deliveryState })}
        keyExtractor={keyExtractor}
        contentContainerStyle={styles.listContainer}
        onRefresh={onRefresh}
        refreshing={isRefreshing}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  listContainer: {
    flexGrow: 1,
    paddingBottom: 16,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  sectionHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  dashedLine: {
    flex: 1,
    opacity: 0.7,
    height: 1,
    borderWidth: 0.5,
    borderColor: colors.divider,
    borderStyle: "dashed",
    borderRadius: 1,
  },
  sectionHeaderTextContainer: {
    backgroundColor: "#EEEEEE",
    alignSelf: "center",
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 40,
  },
  sectionHeaderText: {
    fontSize: 11,
    lineHeight: 15,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.mediumBlack,
  },
  sectionHeader: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  dateText: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Bold",
  },
});

export default Overview;
